import React, { ReactNode, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { showToast } from '~/components/Shared/EcToast/showToast';
import { Box } from '~/eds/src/components/Box/Box';
import { useModal } from '~/eds/src/components/hooks/useModal';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { api } from '~/redux/api/createApi';
import { DocumentClause } from '~/types/Clause';
import { ERROR, SUCCESS } from '~/types/toast.types';

import styles from './styles';

const useEditClauseModal = (
  clause: DocumentClause,
  handlerId: number,
  onClose: () => void,
): [ReactNode, () => void] => {
  const [content, setContent] = useState('');
  const [updateClause, updateClauseResult] =
    api.endpoints.updateClauseContent.useMutation();
  const { isError, isSuccess, isLoading } = updateClauseResult;
  const intl = useIntl();

  const onChangeClauseContent = (text: string | null) => {
    if (text) {
      setContent(text);
    }
  };

  useEffect(() => {
    if (isSuccess) {
      showToast(
        SUCCESS,
        intl.formatMessage({
          id: 'UseEditClauseModal.clauseUpdated',
          defaultMessage: 'Clause updated correctly',
          description: 'Toast message when clause is updated successfully',
        }),
      );
      hideEditModal();
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'UseEditClauseModal.updateFailed',
          defaultMessage: 'Something went wrong, please try again later.',
          description: 'Toast message when clause update fails',
        }),
      );
    }
  }, [isError]);

  useEffect(() => {
    if (clause) {
      setContent(clause.content);
    }
  }, [clause]);

  const [editModal, showEditModal, hideEditModal] = useModal({
    title: intl.formatMessage(
      {
        id: 'UseEditClauseModal.editTitle',
        defaultMessage: 'Edit {title}',
        description:
          'Modal title for editing a clause. Example: title = "Section 1"',
      },
      { title: clause.title },
    ),
    isFullPage: true,
    onCancel: onClose,
    primaryAction: {
      text: intl.formatMessage({
        id: 'UseEditClauseModal.saveButton',
        defaultMessage: 'Save',
        description: 'Save button text in edit clause modal',
      }),
      isLoading,
      onClick: () => {
        updateClause({ clauseId: clause.id, content, handlerId });
      },
    },
    children: (
      <Box styles={styles.editClauseContainer}>
        <TextArea
          name="test"
          value={content}
          onChange={onChangeClauseContent}
        />
      </Box>
    ),
  });

  return [editModal, showEditModal];
};

export default useEditClauseModal;
